import type { CSSProperties, ReactNode } from "react";
import { pathFor } from "../../app/router/routes";
import { useCopy } from "../../shared/i18n";
import { MARKETING_COPY, type MarketingCopy } from "./copy";
import { MARKETING_GAMES } from "./games";
import {
  BookIcon,
  ChildrenIcon,
  PhoneIcon,
  PlayIcon,
  ReportsIcon,
  ShieldIcon,
  TickIcon,
} from "./icons";
import { Reveal } from "./Reveal";
import { schoolsHeroImage, useDocumentSeo, useSiteContent, type ForSchoolsContent } from "./siteContent";
import { DraftBar } from "./sections/DraftBar";
import { useSectionSpy } from "./useSectionSpy";
import { ClassOverview } from "./sections/Audiences";
import { CtaLink } from "./sections/Cta";
import { Faq } from "./sections/Faq";
import { FinalCta } from "./sections/FinalCta";
import { GameCard } from "./sections/GamesShowcase";
import { Privacy } from "./sections/Privacy";
import { Section, SectionHeading } from "./sections/primitives";
import { SiteFooter } from "./sections/SiteFooter";
import { SiteHeader } from "./sections/SiteHeader";
import { TrustStrip } from "./sections/TrustStrip";

export interface ForSchoolsPageProps {
  /** "Log in" — opens the door picker (`#/start`). */
  onSignIn?: () => void;
}

/** One icon and tint per "what your school gets" card, in the copy's order. */
const GETS_ICONS: { icon: ReactNode; tint: string }[] = [
  { icon: <PlayIcon className="h-5 w-5" />, tint: "bg-primary-tint text-primary" },
  { icon: <BookIcon className="h-5 w-5" />, tint: "bg-indigo/10 text-indigo" },
  { icon: <ChildrenIcon className="h-5 w-5" />, tint: "bg-leaf/14 text-kid-secondary-fg" },
  { icon: <ReportsIcon className="h-5 w-5" />, tint: "bg-sky/12 text-sky" },
  { icon: <PhoneIcon className="h-5 w-5" />, tint: "bg-sunshine/18 text-warning" },
  { icon: <ShieldIcon className="h-5 w-5" />, tint: "bg-teal/12 text-teal" },
];

const ROLLOUT_TONES = [
  "bg-primary shadow-[0_5px_0_var(--color-primary-deep)]",
  "bg-sky shadow-[0_5px_0_color-mix(in_srgb,var(--color-sky)_60%,var(--color-ink))]",
  "bg-leaf shadow-[0_5px_0_var(--color-kid-secondary-fg)]",
  "bg-sunshine shadow-[0_5px_0_var(--color-kid-voice-shadow)]",
] as const;

/**
 * `#/for-schools` — the website's page for the person who signs.
 *
 * 🔴 EVERY CLAIM HERE IS ONE THE PRODUCT ALREADY MAKES ELSEWHERE, said once for
 * a school buyer: the games and their NCF-FS mapping (the curriculum registry),
 * the class and roster tools, per-child progress, the parent report, and the
 * camera promise (I1/I5). The rollout is the real one — a school cannot mint its
 * own first admin (`admin-panel.md` §3), so step two is the Kalqy team setting
 * the school up, not a self-serve sign-up form.
 *
 * ⚠️ THE PREVIEWS ARE LABELLED "Sample data", the parent report included. A
 * child's name on a mock-up with no label is the one kind of detail a reader
 * reasonably takes for a real report.
 */
export function ForSchoolsPage({ onSignIn }: ForSchoolsPageProps) {
  const t = useCopy(MARKETING_COPY);
  const spy = useSectionSpy();
  /*
   * The editable words (P3-21): built-in copy first, then what an operator
   * published — see `siteContent.ts`. The sample previews (class overview,
   * parent report) stay in `copy.ts`; they are illustrations, not claims.
   */
  const { content: c, draft } = useSiteContent("for-schools");
  useDocumentSeo(c.seo);

  return (
    <div className="dash-text min-h-screen overflow-x-clip bg-surface font-dash text-ink selection:bg-primary-tint selection:text-primary">
      <SiteHeader t={t} spy={spy} onSignIn={onSignIn} standalone overlay current="for-schools" />

      <main>
        <SchoolsHero t={t} c={c} />
        <TrustStrip t={t} />

        {/* ── What your school gets ─────────────────────────────────────── */}
        <Section className="pt-24 lg:pt-32">
          <SectionHeading label={c.gets.label} heading={c.gets.heading} accentClass="bg-primary" labelClass="text-primary" />
          <ul className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-5">
            {c.gets.items.map((item, i) => (
              /* By position: edited titles may repeat, and each slot has its own icon. */
              <li key={i}>
                <Reveal delay={(i % 3) * 80} className="h-full">
                  <div className="flex h-full gap-4 rounded-xl border border-line bg-surface p-6 transition-colors duration-200 ease-dash hover:border-primary/30 hover:bg-cream">
                    <span
                      aria-hidden="true"
                      className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-lg ${GETS_ICONS[i]?.tint ?? ""}`}
                    >
                      {GETS_ICONS[i]?.icon}
                    </span>
                    <span>
                      <span className="block font-dash text-dash-md font-extrabold text-ink">{item.title}</span>
                      <span className="mt-1 block font-dash text-dash-sm text-ink-soft">{item.body}</span>
                    </span>
                  </div>
                </Reveal>
              </li>
            ))}
          </ul>
        </Section>

        {/* ── Getting started ───────────────────────────────────────────── */}
        <div className="mt-24 rounded-t-[2.5rem] bg-canvas pt-20 pb-24 lg:mt-32 lg:rounded-t-[3.5rem] lg:pt-24 lg:pb-28">
          <Section>
            <SectionHeading
              label={c.rollout.label}
              heading={c.rollout.heading}
              accentClass="bg-leaf"
              labelClass="text-success"
            />
            <ol className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5">
              {c.rollout.steps.map((step, i) => (
                <li key={i}>
                  <Reveal delay={i * 90} className="h-full">
                    <div className="relative h-full rounded-xl border border-line bg-surface p-6">
                      <span
                        aria-hidden="true"
                        className={`flex h-12 w-12 items-center justify-center rounded-pill font-kid text-kid-sm font-extrabold text-surface ${ROLLOUT_TONES[i] ?? ""}`}
                      >
                        {i + 1}
                      </span>
                      <span className="mt-4 block font-dash text-dash-lg font-extrabold text-ink">{step.title}</span>
                      <span className="mt-1 block font-dash text-dash-sm text-ink-soft">{step.body}</span>
                    </div>
                  </Reveal>
                </li>
              ))}
            </ol>
            <Reveal delay={360} className="mt-8">
              <CtaLink href={pathFor("contact-sales")} variant="primary" size="md">
                {t.cta.bookSchoolDemo}
              </CtaLink>
            </Reveal>
          </Section>
        </div>

        {/* ── What teachers and parents see ─────────────────────────────── */}
        <Section className="pt-24 lg:pt-32">
          <SectionHeading label={c.sees.label} heading={c.sees.heading} accentClass="bg-success" labelClass="text-success" />
          <div className="mt-10 grid gap-5 lg:grid-cols-2 lg:gap-6">
            <Reveal variant="left">
              <figure className="h-full rounded-2xl border border-line bg-surface p-6 sm:p-8">
                <ClassOverview t={t} />
                <figcaption className="mt-5 font-dash text-dash-md font-extrabold text-ink">{c.sees.teacherCaption}</figcaption>
              </figure>
            </Reveal>
            <Reveal variant="right">
              <figure className="h-full rounded-2xl border border-line bg-surface p-6 sm:p-8">
                <ParentReport t={t} />
                <figcaption className="mt-5 font-dash text-dash-md font-extrabold text-ink">{c.sees.parentCaption}</figcaption>
              </figure>
            </Reveal>
          </div>
        </Section>

        {/* ── The games ─────────────────────────────────────────────────── */}
        <Section className="pt-24 pb-28 lg:pt-32 lg:pb-36">
          <SectionHeading label={c.games.label} heading={c.games.heading} accentClass="bg-leaf" labelClass="text-success" />
          <ul className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6">
            {MARKETING_GAMES.map((game, i) => (
              <li key={game.id}>
                <Reveal delay={(i % 3) * 80} className="h-full">
                  <GameCard t={t} game={game} />
                </Reveal>
              </li>
            ))}
          </ul>
        </Section>

        <Privacy t={t} />
        <Faq t={t} />
        <FinalCta t={t} heading={c.finalHeading} />
      </main>

      <div className="mt-24 lg:mt-32">
        <SiteFooter t={t} />
      </div>
      {draft ? <DraftBar version={draft.version} back="#/platform/website?tab=for-schools" /> : null}
    </div>
  );
}

/** The hero: the classroom itself, full width, with the page's promise over it. */
function SchoolsHero({ t, c }: { t: MarketingCopy; c: ForSchoolsContent }) {
  const s = c.hero;
  const image = schoolsHeroImage(s.image);
  return (
    <section className="relative isolate overflow-hidden bg-sky">
      <picture>
        <source media="(max-width: 767px)" srcSet={image.small} />
        <img
          src={image.src}
          alt={s.imageAlt}
          decoding="async"
          fetchPriority="high"
          className="absolute inset-0 -z-10 h-full w-full object-cover object-[60%_center]"
        />
      </picture>
      <div aria-hidden="true" className="kq-hero-shade pointer-events-none absolute inset-0 -z-10" />

      <div className="kq-on-photo kq-hero-frame mx-auto flex w-full max-w-[1240px] flex-col justify-center px-5 pt-28 pb-24 text-surface sm:px-6 sm:pt-32 lg:px-10 lg:pt-32 lg:pb-32">
        <p className="kq-rise-in inline-flex w-fit items-center gap-2.5 rounded-pill border border-surface/55 bg-surface/20 py-1.5 pr-4 pl-2.5 font-dash text-dash-xs font-extrabold backdrop-blur-md">
          <span aria-hidden="true" className="h-2.5 w-2.5 rounded-pill bg-leaf" />
          {s.eyebrow}
        </p>
        <h1
          className="kq-rise-in kq-hero-sm mt-5 max-w-[14ch] font-kid font-extrabold"
          style={{ "--kq-delay": "100ms" } as CSSProperties}
        >
          {s.heading}
        </h1>
        <p
          className="kq-rise-in mt-5 max-w-[44ch] font-dash text-dash-lg font-bold text-surface/95"
          style={{ "--kq-delay": "200ms" } as CSSProperties}
        >
          {s.sub}
        </p>
        <div
          className="kq-rise-in mt-8 flex flex-col gap-4 sm:flex-row sm:items-center"
          style={{ "--kq-delay": "300ms" } as CSSProperties}
        >
          <CtaLink href={pathFor("contact-sales")} variant="primary">
            {t.cta.bookSchoolDemo}
          </CtaLink>
          <CtaLink href={pathFor("sky-quest-play")} variant="glass" icon="play">
            {t.cta.playSkyQuestFree}
          </CtaLink>
        </div>
        <ul className="kq-rise-in mt-7 flex flex-wrap gap-x-5 gap-y-2" style={{ "--kq-delay": "400ms" } as CSSProperties}>
          {s.proof.map((line, i) => (
            <li key={i} className="inline-flex items-center gap-2 font-dash text-dash-xs font-extrabold">
              <span aria-hidden="true" className="flex h-5 w-5 items-center justify-center rounded-pill bg-surface/25">
                <TickIcon className="h-3.5 w-3.5" />
              </span>
              {line}
            </li>
          ))}
        </ul>
      </div>

      <svg
        aria-hidden="true"
        viewBox="0 0 1440 96"
        preserveAspectRatio="none"
        className="absolute inset-x-0 -bottom-px z-10 h-12 w-full text-surface sm:h-20"
      >
        <path fill="currentColor" d="M0 58C180 26 360 18 560 40C760 62 920 86 1120 70C1260 59 1360 40 1440 30V96H0Z" />
      </svg>
    </section>
  );
}

/** A parent's weekly report, drawn as the phone screen it arrives on. Labelled sample data. */
function ParentReport({ t }: { t: MarketingCopy }) {
  const r = t.schoolsPage.parentReport;
  return (
    <div className="mx-auto max-w-[19rem] rounded-[2rem] border-[6px] border-ink bg-cream p-4 shadow-raised">
      <div className="flex items-center justify-between">
        <span className="font-dash text-dash-xs font-bold text-muted">{r.week}</span>
        <span className="rounded-pill border border-line bg-surface px-2 py-0.5 font-dash text-dash-xs font-extrabold text-muted">
          {t.sampleData}
        </span>
      </div>
      <p className="mt-2 font-kid text-kid-sm leading-none font-extrabold text-ink">{r.child}</p>
      <ul className="mt-4 space-y-2">
        {r.lines.map((line, i) => (
          <li key={line} className="flex items-start gap-2.5 rounded-lg bg-surface p-2.5 font-dash text-dash-xs font-bold text-ink-soft">
            <span
              aria-hidden="true"
              className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-pill text-surface ${
                i === 2 ? "bg-primary" : "bg-success"
              }`}
            >
              <TickIcon className="h-3 w-3" />
            </span>
            {line}
          </li>
        ))}
      </ul>
    </div>
  );
}
